import {
  useEffect,
  useRef,
  useState,
  FC,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
} from 'react'
import styled from 'styled-components'
import { Manager, Reference, Popper } from 'react-popper'

import { heightLarge as navbarHeight } from 'layout/Navbar/constants'
import { filterBarHeight } from '../../../components/layout/ItemGridWithMap/Desktop'
import ChevronDownIcon from '../../../components/icons/ChevronDown'
import CrossOutIcon from '../../../components/icons/CrossOut'
import InvisibleButton from '../../../components/display/InvisibleButton'

export const Wrapper = styled.div``

export const FilterButtonInner = styled(InvisibleButton)`
  display: flex;
  align-items: center;

  flex-grow: 1;
  height: 100%;
  padding: 0 12px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const Badge = styled.span`
  height: 22px;
  width: 22px;
  margin-right: 12px;

  font-size: 14px;
  line-height: 22px;

  color: #333;
  background-color: ${({ theme }) => theme.brand.sand};
  border-radius: 50%;
  flex-shrink: 0;
`

export const IconButton = styled(InvisibleButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 20px;
  flex-shrink: 0;
`

export const ButtonWrapper = styled.div<{ isActive: boolean }>`
  appearance: none;
  display: flex;
  align-items: center;

  height: 40px;

  background-color: white;
  border: 1px solid #d5d5d5;
  border-radius: 6px;

  ${({ isActive, theme }) =>
    isActive &&
    `
    color: #fff;
    background-color: ${theme.brand.jungle};
    border-color: ${theme.brand.jungle};
  `}
`

const ButtonIconWrapper = styled.div`
  /* margin-right: 12px; */
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
    cursor: pointer;
  }
`

const Label = styled.span`
  margin-right: 8px;
`

const ContentWrapper = styled.div`
  /* to accommodate absolute-positioned "apply" button wrapper, which is aligned to bottom of the parent element, when
  the parent element is scrolled all the way down */
  padding-bottom: 52px;
`

const ApplyWrapper = styled.div<{ $showBorder?: boolean }>`
  z-index: 1; /* without explicit z-index higher than content wrapper, the latter receives click events */
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  padding: 16px;

  display: flex;
  justify-content: flex-end;

  ${({ $showBorder, theme }) =>
    $showBorder &&
    `
    border-top: 1px solid ${theme?.['-ds-neutral-300']?.value};
    background-color: ${theme?.['-ds-neutral-100']?.value};
  `}
`

const Panel = styled.div`
  position: relative;
  top: 19px;
  min-width: 400px;
  max-width: 800px;
  padding: 24px 0 16px 24px;
  background-color: #fff;
  box-shadow: 0 2px 4px 0 rgba(31, 23, 23, 0.15);
  border: 1px solid #d5d5d5;
  border-radius: 6px;
  z-index: 1;

  .tether-pinned-left & {
    left: 22px;

    &:after,
    &:before {
      left: 40px;
    }
  }

  .tether-pinned-right & {
    right: 22px;
    &:after,
    &:before {
      left: auto;
      right: 40px;
    }
  }

  // Maximum panel height is viewport height less filter bar height, desktop navbar height, and some additional
  // margin so that it doesn't sit hard against the edge of the viewport:
  max-height: calc(
    100vh - ${parseInt(filterBarHeight, 10)}px - ${navbarHeight}px - 36px
  );
  display: flex;
  flex-direction: column;

  ${ContentWrapper}, ${ApplyWrapper} {
    padding-right: 24px;
  }

  ${ContentWrapper} {
    flex: 1 1 auto;
    overflow-y: auto;
    padding-top: 8px;
    padding-left: 6px;
  }

  ${ApplyWrapper} {
    flex: 0 0 auto;
  }
`

const ApplyButton = styled.button.attrs({ type: 'button' })`
  padding: 8px 0;
  padding-left: 16px;

  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.brand.jungle};
  background-color: rgba(255, 255, 255, 1);
  border: none;
`

const Arrow = styled.div`
  position: absolute;
  top: 0;
  height: 12px;
  width: 12px;

  &:after,
  &:before {
    bottom: 100%;
    left: 50%;
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  &:after {
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: #ffffff;
    border-width: 12px;
    margin-left: -12px;
  }
  &:before {
    border-color: rgba(213, 213, 213, 0);
    border-bottom-color: #d5d5d5;
    border-width: 13px;
    margin-left: -13px;
  }
`

type HandlerFn = () => any

type FilterButtonProps = {
  isActive: boolean
  onApply: HandlerFn
  onClear: HandlerFn
  onReset: HandlerFn
  label?: ReactNode
  badge?: ReactNode
  panelActionsBorder?: boolean
}

const FilterButton: FC<PropsWithChildren<FilterButtonProps>> = ({
  isActive,
  onApply,
  onClear,
  onReset,
  label,
  badge,
  children,
  panelActionsBorder,
}) => {
  const [open, setOpen] = useState(false)

  const panelRef = useRef<HTMLDivElement>()

  const handleClose = () => {
    setOpen(false)
    onReset()
  }

  const handleClearIconClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    onClear()
    handleClose()
  }

  const handleToggle = () => {
    setOpen(!open)
  }

  const handleDocumentClick = (e: Event) => {
    if (panelRef.current?.contains(e.target as Node) !== true) {
      onApply()
      handleClose()
    }
  }

  const handleApply = () => {
    onApply()
    handleClose()
  }

  useEffect(() => {
    if (open) {
      document.addEventListener('click', handleDocumentClick, { capture: true })
    } else {
      document.removeEventListener('click', handleDocumentClick, {
        capture: true,
      })
    }

    return () => {
      document.removeEventListener('click', handleDocumentClick, {
        capture: true,
      })
    }
  }, [open])

  const modifiers = [
    {
      name: 'preventOverflow',
      options: {
        padding: 22,
        enabled: true,
      },
    },
  ]
  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <Wrapper ref={ref}>
            <ButtonWrapper isActive={isActive}>
              <FilterButtonInner onClick={handleToggle}>
                <>
                  {badge && <Badge>{badge}</Badge>}
                  <Label>{label}</Label>
                </>
                {isActive ? (
                  <IconButton
                    onClick={handleClearIconClick}
                    aria-label="Clear filter"
                  >
                    <ButtonIconWrapper>
                      <CrossOutIcon />
                    </ButtonIconWrapper>
                  </IconButton>
                ) : (
                  <ButtonIconWrapper>
                    <ChevronDownIcon />
                  </ButtonIconWrapper>
                )}
              </FilterButtonInner>
            </ButtonWrapper>
          </Wrapper>
        )}
      </Reference>
      <Popper placement="bottom" modifiers={modifiers}>
        {({ ref, style, placement, arrowProps }) =>
          open && (
            <div ref={ref} style={style} data-placement={placement}>
              <Panel ref={panelRef}>
                <ContentWrapper>{children}</ContentWrapper>
                <ApplyWrapper $showBorder={panelActionsBorder}>
                  <ApplyButton onClick={handleApply}>Apply</ApplyButton>
                </ApplyWrapper>
                <Arrow ref={arrowProps.ref} style={arrowProps.style} />
              </Panel>
            </div>
          )
        }
      </Popper>
    </Manager>
  )
}

export default FilterButton
